import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "details-sidebar-expanded"

const COL_COLLAPSED = "w-10"
const COL_PEEK = "w-12"
const COL_EXPANDED = "w-[30rem]"

export default class extends Controller {
  static targets = ["column", "rail", "panel"]

  connect() {
    this.closeOnEscape = this.closeOnEscape.bind(this)
    this.expanded = localStorage.getItem(STORAGE_KEY) === "true"
    this.peeking = false
    this.applyExpansion({ animate: false })
  }

  disconnect() {
    document.removeEventListener("keydown", this.closeOnEscape)
  }

  toggle() {
    this.expanded = !this.expanded
    localStorage.setItem(STORAGE_KEY, this.expanded)
    if (!this.expanded) this.dispatch("close")
    this.applyExpansion({ animate: true })
  }

  peekShow() {
    if (this.expanded || this.peeking) return
    this.peeking = true
    this._setColWidth(COL_PEEK)

    // Render panel at opacity-0, then transition up to opacity-20 on the next frame.
    const panel = this.panelTarget
    panel.classList.remove("hidden", "opacity-20", "opacity-100")
    panel.classList.add("flex", "opacity-0")
    this.railTarget.classList.add("hidden")

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.peeking) return
        panel.classList.remove("opacity-0")
        panel.classList.add("opacity-20")
      })
    })
  }

  peekHide() {
    if (this.expanded || !this.peeking) return
    this.peeking = false
    this._setColWidth(COL_COLLAPSED)

    // Fade panel out before hiding it on column transitionend.
    this.panelTarget.classList.remove("opacity-20")
    this.panelTarget.classList.add("opacity-0")
    this._hidePanelOnTransitionEnd()
  }

  expandFromPeek() {
    if (!this.peeking) return
    this.toggle()
  }

  applyExpansion({ animate }) {
    this.peeking = false
    const col = this.columnTarget

    if (!animate) col.classList.remove("transition-[width]")

    if (this.expanded) {
      this._setColWidth(COL_EXPANDED)
      this._showPanel("opacity-100")
      document.addEventListener("keydown", this.closeOnEscape)
    } else {
      this._setColWidth(COL_COLLAPSED)
      if (animate) {
        this._hidePanelOnTransitionEnd()
      } else {
        this._hidePanelImmediate()
      }
      document.removeEventListener("keydown", this.closeOnEscape)
    }

    if (!animate)
      requestAnimationFrame(() => col.classList.add("transition-[width]"))
  }

  _setColWidth(targetClass) {
    const col = this.columnTarget
    col.classList.remove(COL_COLLAPSED, COL_PEEK, COL_EXPANDED)
    col.classList.add(targetClass)
  }

  _showPanel(opacityClass) {
    const panel = this.panelTarget
    panel.classList.remove("hidden", "opacity-0", "opacity-20", "opacity-100")
    panel.classList.add("flex", opacityClass)
    this.railTarget.classList.add("hidden")
  }

  _hidePanelImmediate() {
    const panel = this.panelTarget
    panel.classList.add("hidden")
    panel.classList.remove("flex", "opacity-0", "opacity-20", "opacity-100")
    this.railTarget.classList.remove("hidden")
  }

  _hidePanelOnTransitionEnd() {
    const swap = () => {
      if (this.expanded || this.peeking) return
      this._hidePanelImmediate()
    }
    this.columnTarget.addEventListener("transitionend", swap, { once: true })
  }

  closeOnEscape(e) {
    if (e.key === "Escape" && this.expanded) this.toggle()
  }
}
